<template>
    <div>
        <div class="flex items-center justify-between bg-gray-100 p-4">
            <div class="font-medium text-lg">{{ selectedTransactionRequest ? "Update Billing Request" : "Billing Requests" }}</div>
            <i class="icon mdi mdi-close right-side-toggle cursor-pointer hover:text-indigo-600 transition duration-300"
               @click="closePanel()"></i>
        </div>

        <div class="px-2 py-4 bg-white h-screen " :class="notSmallScreen ? 'overflow-x-scroll' : 'overflow-auto'" v-if="!selectedTransactionRequest && !showCreateForm">
            <div v-if="transactionRequests && transactionRequests.length > 0 && !selectedTransactionRequest">
                <table class="w-full">
                    <thead class="border-b border-gray-100">
                        <tr>
                            <th class="p-3 align-middle font-medium">
                                <div>Status</div>
                            </th>

                            <th class="p-3 align-top" style="min-width: 200px">
                                <div class="font-medium">Effective Date</div>
                                <div>Description</div>
                            </th>

                            <th class="p-3 align-top">
                                <div class="font-medium">Beneficiary</div>
                                <div>Reference</div>
                            </th>

                            <th class="p-3 align-top text-right">
                                <div class="text-indigo-600 font-medium">Total</div>
                                <div class="text-gray-500">Vat</div>
                            </th>

                            <th class="p-3 align-top">
                                <div class="font-medium">Billed On</div>
                                <div>RAMS Transaction ID</div>
                            </th>

                            <th class="p-3 align-top" style="min-width: 120px">
                                <div class="font-medium">Rejected On</div>
                                <div class="whitespace-nowrap">Rejection Message</div>
                            </th>

                            <!-- More options button for rows, but new button in header-->
                            <th class="p-3 relative">
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr v-for="(transactionRequest, index) in transactionRequests" :class="index % 2 === 1 ? 'bg-gray-50' : ''">
                            <td class="p-3">
                                <badge :class="statusClass(transactionRequest.status)">{{ transactionRequest.status | capitalize }}</badge>
                            </td>

                            <td class="p-3">
                                <div class="font-medium"> {{ transactionRequest.effective_date | date }}</div>
                                <div class="text-xs flex flex-wrap">{{ transactionRequest.description }}</div>
                            </td>

                            <td class="p-3">
                                <div class="font-medium">{{ transactionRequest.beneficiary_details ? transactionRequest.beneficiary_details.name : "Not Selected" }}</div>
                                <div>{{ transactionRequest.payment_reference }}</div>
                            </td>

                            <td class="p-3 text-right">
                                <div class="text-indigo-600 font-medium">R {{ transactionRequest.total | money }}</div>
                                <div class="text-gray-500">R {{ transactionRequest.vat | money }}</div>
                            </td>

                            <td class="p-3">
                                <div class="font-medium" v-show="transactionRequest.billed_at">{{ transactionRequest.billed_at | datetime }}</div>
                                <div>{{ transactionRequest.resultant_transaction_id }}</div>
                            </td>

                            <td class="p-3">
                                <div v-show="transactionRequest.rejected_at" class="font-medium">{{ transactionRequest.rejected_at | datetime }}</div>
                                <div>{{ transactionRequest.rejection_message }}</div>
                            </td>

                            <td class="relative p-3">
                                <template v-if="canUpdateTransactionRequest(transactionRequest)">
                                    <button @click.prevent="openDropdownOptions(transactionRequest.id)">
                                        <icon-vertical-dots class="h-4 w-4 text-gray-500"></icon-vertical-dots>
                                    </button>

                                    <div class="absolute right-8 top-10 z-40" v-if="showOptionsDropdownForTransaction === transactionRequest.id"
                                         v-click-outside="hideDropDownOptions">
                                        <div class="text-left bg-white py-1 shadow-lg rounded-md flex flex-col border-t-2 border-brand">
                                            <button
                                                class="outline-none flex py-1 px-2 hover:bg-gray-100 transition duration-150"
                                                @click.prevent="toggleCreateEditTransactionRequest(transactionRequest)">
                                                Edit
                                            </button>
                                            <button
                                                class="outline-none flex text-red-600 py-1 px-2 hover:bg-gray-100 transition duration-150"
                                                @click.prevent="showDeleteConfirmationDialog(true,transactionRequest)">
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </template>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-else-if="loading" class="text-gray-500 flex items-center justify-center pt-10">
                <icon-refresh class="h-4 w-4 text-indigo-600 animate-spin"/>&nbsp;Loading Billing Requests...
            </div>

            <div v-else class="justify-center flex flex-col space-y-4 mt-12">
                <span class="text-gray-500 flex justify-center">No Billing Requests Yet</span>
            </div>

            <div class="bg-white flex mx-auto justify-center rounded-md mt-12">
                <secondary-button @click.prevent="showCreateForm = true">
                    New Request
                </secondary-button>
            </div>

        </div>

        <transaction-request-create-update-form
            class="bg-white p-4"
            v-if="selectedTransactionRequest || showCreateForm"
            :transaction-request="selectedTransactionRequest"
            :parent-model="parentModel"
            :contact-group-id="contactGroupId"
            :ticket-id="modelType === 'App\\Ticket' ? modelId : null"
            :inspection-id="modelType === 'App\\Inspection' ? modelId : null"
            :lease-id="leaseId"
            :asset-id="assetId"
            dropzone-url="/dummy/url"
            :accepted-files="documentMimeTypes"
            :max-size="20"
            :clickable="true"
            @update-transaction-request-list="updateTransactionRequestsList"
            @archive-contact-group="archiveContactGroup()"
            @close="toggleCreateEditTransactionRequest">
        </transaction-request-create-update-form>

        <confirmation-dialog
            :show="deleteConfirmationDialogVisible"
            title="Delete Transaction Request"
            subtitle="Are you sure you want to delete this transaction request? NOTE: It will be deleted in Rams & RedRabbit."
            :show-icon="false"
            proceed-button-text="Yes, Delete"
            cancel-button-text="No, Don't Delete"
            proceed-button-colour="indigo-600"
            proceed-button-hover-colour="indigo-500"
            @cancelled="showDeleteConfirmationDialog(null)"
            @proceed="deleteTransactionRequest"
        ></confirmation-dialog>
    </div>
</template>

<script>
import AcceptedMimeTypes from "../../mixins/accepted-mime-types";

export default {
    name: "TransactionRequestList",

    mixins: [AcceptedMimeTypes],

    props: {
        modelType: {
            required: true,
            type: String,
            description: "The model type to load the transaction requests from"
        },
        modelId: {
            required: true,
            type: Number,
            description: "The model id to load the transaction requests from"
        },
        parentModel: {
            required: true,
            type: Object,
            description: "Used to pass in the parent model where this feature is opened from. i.e. Ticket"
        },
        assetId: {
            required: true,
            description: "The asset id used for creating a transaction request"
        },
        contactGroupId: {
            required: false,
            description: "The contact group id used for creating a transaction request",
            default: null,
        },
        leaseId: {
            required: false,
            description: "The lease id used for creating a transaction request",
            default: null,
        }
    },

    data () {
        return {
            loading: true,
            transactionRequests: null,
            selectedTransactionRequest: null,
            showCreateForm: false,
            showOptionsDropdownForTransaction: false,
            deleteConfirmationDialogVisible: false,
            requestToDelete: null
        };
    },

    mounted () {
        this.fetchTransactionRequests();
    },

    methods: {
        fetchTransactionRequests () {
            this.loading = true;

            axios.get(`/transaction-requests/`, {
                params: {
                    model_type: this.modelType,
                    model_id: this.modelId,
                }
            })
            .then(response => {
                this.transactionRequests = response.data;
                this.loading = false;
            })
            .catch(error => {
                this.loading = false;
                toastr.error(`Error fetching billing requests. ${error.message}`);
            });
        },

        showDeleteConfirmationDialog (shouldDeleteTransactionRequest = true, transactionRequest = null) {
            this.requestToDelete = shouldDeleteTransactionRequest ? transactionRequest : null;
            this.deleteConfirmationDialogVisible = shouldDeleteTransactionRequest;
        },

        deleteTransactionRequest () {
            this.hideDropDownOptions();
            App.delete(`/transaction-requests/destroy/${this.requestToDelete.id}`, new AppForm({}))
            .then(response => {
                let index = this.transactionRequests.findIndex(tr => tr.id === this.requestToDelete.id);
                if (response.deleted_at) {
                    this.showDeleteConfirmationDialog(false, null);
                    this.transactionRequests.splice(index, 1);
                    toastr.error("Successfully Deleted Billing Request");
                } else {
                    this.transactionRequests.splice(index, 1, response);
                    toastr.error("Billing Request updated from RAMS");
                }
                this.requestToDelete = null;
            }).catch(error => {
                toastr.error("Unable to delete billing request. " + error.message);
            });
        },

        statusClass (status) {
            switch (status) {
                case "new":
                    return "bg-indigo-400 text-white";
                case "billed":
                    return "bg-green-400 text-white";
                case "rejected":
                    return "bg-red-400 text-white";
                case "cancelled":
                    return "bg-yellow-400 text-white";
                default:
                    return "bg-gray-400 text-white";
            }
        },

        updateTransactionRequestsList (transactionRequest) {
            let index = this.transactionRequests.findIndex(tr => tr.id === transactionRequest.id);
            if (index >= 0) {
                this.transactionRequests.splice(index, 1, transactionRequest);
            } else {
                this.transactionRequests.push(transactionRequest);
            }
        },

        closePanel () {
            this.$emit("close");
        },

        openDropdownOptions (transactionRequestId) {
            this.showOptionsDropdownForTransaction = transactionRequestId;
        },

        hideDropDownOptions () {
            this.showOptionsDropdownForTransaction = null;
        },

        toggleCreateEditTransactionRequest (transactionRequest = null) {
            // Clear already selected, otherwise select
            this.hideDropDownOptions();
            this.selectedTransactionRequest = this.selectedTransactionRequest ? null : transactionRequest;
            this.showCreateForm = !this.showCreateForm;
        },

        canUpdateTransactionRequest (transactionRequest) {
            return transactionRequest.billed_at === null && transactionRequest.rejected_at === null && transactionRequest.status === "new";
        },

        archiveContactGroup(){
            this.closePanel();
            this.$emit('archive-contact-group');
        }
    },
};
</script>

<style scoped>

</style>
